import { rateToApy, SECONDS_PER_YEAR } from './parsers'

export type RoundingDirection = 'Up' | 'Down'

/**
 * Library to manage fixed-point arithmetic.
 * https://github.com/morpho-org/morpho-blue/blob/main/src/libraries/MathLib.sol
 */
export namespace MathLib {
  export const WAD = 1_000000000000000000n
  export const RAY = 1_000000000000000000000000000n

  export const MAX_UINT_256 = maxUint(256)
  export const MAX_UINT_160 = maxUint(160)
  export const MAX_UINT_128 = maxUint(128)
  export const MAX_UINT_48 = maxUint(48)

  export function maxUint(nBits: number) {
    if (nBits % 4 !== 0) throw new Error(`Invalid number of bits: ${nBits}`)

    return BigInt(`0x${'f'.repeat(nBits / 4)}`)
  }

  /**
   * Returns the absolute value of a number
   * @param a The number
   */
  export function abs(a: bigint) {
    a = BigInt(a)

    return a >= 0 ? a : -a
  }

  /**
   * Returns the smallest number given as param
   * @param x The first number
   * @param y The second number
   */
  export function min(...xs: bigint[]) {
    return xs.map(BigInt).reduce((x, y) => (x <= y ? x : y))
  }

  /**
   * Returns the greatest number given as param
   * @param x The first number
   * @param y The second number
   */
  export function max(...xs: bigint[]) {
    return xs.map(BigInt).reduce((x, y) => (x <= y ? y : x))
  }

  /**
   * Returns the subtraction of b from a, floored to zero if negative
   * @param x The first number
   * @param y The second number
   */
  export function zeroFloorSub(x: bigint, y: bigint) {
    x = BigInt(x)
    y = BigInt(y)

    return x <= y ? 0n : x - y
  }

  /**
   * Perform the WAD-based multiplication of 2 numbers, rounded down
   * @param x The first number
   * @param y The second number
   */
  export function wMulDown(x: bigint, y: bigint) {
    return MathLib.wMul(x, y, 'Down')
  }

  /**
   * Perform the WAD-based multiplication of 2 numbers, rounded up
   * @param x The first number
   * @param y The second number
   */
  export function wMulUp(x: bigint, y: bigint) {
    return MathLib.wMul(x, y, 'Up')
  }

  /**
   * Perform the WAD-based multiplication of 2 numbers with a provided rounding direction
   * @param x The first number
   * @param y The second number
   */
  export function wMul(x: bigint, y: bigint, rounding: RoundingDirection) {
    return MathLib.mulDiv(x, y, MathLib.WAD, rounding)
  }

  /**
   * Perform the WAD-based division of 2 numbers, rounded down
   * @param x The first number
   * @param y The second number
   */
  export function wDivDown(x: bigint, y: bigint) {
    return MathLib.wDiv(x, y, 'Down')
  }

  /**
   * Perform the WAD-based multiplication of 2 numbers, rounded up
   * @param x The first number
   * @param y The second number
   */
  export function wDivUp(x: bigint, y: bigint) {
    return MathLib.wDiv(x, y, 'Up')
  }

  /**
   * Perform the WAD-based multiplication of 2 numbers with a provided rounding direction
   * @param x The first number
   * @param y The second number
   */
  export function wDiv(x: bigint, y: bigint, rounding: RoundingDirection) {
    return MathLib.mulDiv(x, MathLib.WAD, y, rounding)
  }

  /**
   * Multiply two numbers and divide by a denominator, rounding down the result
   * @param x The first number
   * @param y The second number
   * @param denominator The denominator
   */
  export function mulDivDown(x: bigint, y: bigint, denominator: bigint) {
    x = BigInt(x)
    y = BigInt(y)
    denominator = BigInt(denominator)
    if (denominator === 0n) throw Error('MathLib: DIVISION_BY_ZERO')

    return (x * y) / denominator
  }

  /**
   * Multiply two numbers and divide by a denominator, rounding up the result
   * @param x The first number
   * @param y The second number
   * @param denominator The denominator
   */
  export function mulDivUp(x: bigint, y: bigint, denominator: bigint) {
    x = BigInt(x)
    y = BigInt(y)
    denominator = BigInt(denominator)
    if (denominator === 0n) throw Error('MathLib: DIVISION_BY_ZERO')

    const roundup = (x * y) % denominator > 0 ? 1n : 0n

    return (x * y) / denominator + roundup
  }

  export function mulDiv(
    x: bigint,
    y: bigint,
    denominator: bigint,
    rounding: RoundingDirection,
  ) {
    return MathLib[`mulDiv${rounding}`](x, y, denominator)
  }

  /**
   * The sum of the first three non-zero terms of a Taylor expansion of e^(nx) - 1,
   * to approximate a continuously compounded interest rate.
   *
   * @param x The base of the exponent
   * @param n The exponent
   */
  export function wTaylorCompounded(x: bigint, n: bigint) {
    const firstTerm = BigInt(x) * BigInt(n)
    const secondTerm = MathLib.mulDivDown(
      firstTerm,
      firstTerm,
      2n * MathLib.WAD,
    )
    const thirdTerm = MathLib.mulDivDown(
      secondTerm,
      firstTerm,
      3n * MathLib.WAD,
    )

    return firstTerm + secondTerm + thirdTerm
  }

  /**
   * Converts a WAD-based quantity to a RAY-based quantity.
   * @param x The WAD-based quantity.
   */
  export function wToRay(x: bigint) {
    return BigInt(x) * 1_000000000n
  }

  /**
   * Returns the market's utilization rate (scaled by WAD).
   * @param market The market state.
   */
  export function getUtilization({
    totalSupplyAssets,
    totalBorrowAssets,
  }: {
    totalSupplyAssets: bigint
    totalBorrowAssets: bigint
  }) {
    if (totalSupplyAssets === 0n) {
      if (totalBorrowAssets > 0n) return MathLib.MAX_UINT_256

      return 0n
    }

    return MathLib.wDivDown(totalBorrowAssets, totalSupplyAssets)
  }

  /**
   * The market's instantaneous supply-side Annual Percentage Yield (APY) at the given timestamp,
   * if the state remains unchanged (not accrued) (scaled by WAD).
   * @param timestamp The timestamp at which to calculate the supply APY.
   * Must be greater than or equal to `lastUpdate`.
   * Defaults to `Time.timestamp()` (returns the current supply APY).
   */
  export function getSupplyApy(
    borrowApy: bigint,
    utilization: bigint,
    fee: bigint,
  ) {
    if (!utilization) return 0n
    return MathLib.wMulUp(
      MathLib.wMulDown(borrowApy, utilization),
      MathLib.WAD - fee,
    )
  }

  /**
   * Returns the rates that _would_ apply to interest accrual for borrowers of this market,
   * if `accrueInterest` was called at the given timestamp (scaled by WAD).
   * @param timestamp The timestamp at which to calculate the accrual borrow rate.
   * Must be greater than or equal to `lastUpdate`.
   * Defaults to `Time.timestamp()` (returns the current accrual borrow rate).
   */
  function getAccrualBorrowRates(
    rateAtTarget: bigint | null,
    utilization: bigint,
    _timestamp: number = Math.floor(Date.now() / 1000),
    _lastUpdate: number,
  ):
    | {
        elapsed: bigint
        avgBorrowRate: bigint
        endBorrowRate: bigint
        endRateAtTarget?: bigint
      }
    | undefined {
    const timestamp = BigInt(_timestamp)

    const elapsed = timestamp - BigInt(_lastUpdate)
    if (elapsed < 0n) return undefined

    if (rateAtTarget == null)
      return {
        elapsed,
        avgBorrowRate: 0n,
        endBorrowRate: 0n,
      }

    return {
      elapsed,
      ..._getBorrowRate(utilization, rateAtTarget, elapsed),
    }
  }

  /**
   */
  function getEndBorrowRate(
    rateAtTarget: bigint | null,
    utilization: bigint,
    _timestamp: number = Math.floor(Date.now() / 1000),
    _lastUpdate: number,
  ) {
    return getAccrualBorrowRates(
      rateAtTarget,
      utilization,
      _timestamp,
      _lastUpdate,
    )?.endBorrowRate
  }

  export function getBorrowApy(
    rateAtTarget: bigint | null,
    utilization: bigint,
    _timestamp: number = Math.floor(Date.now() / 1000),
    _lastUpdate: number,
  ) {
    const borrowRate = getEndBorrowRate(
      rateAtTarget,
      utilization,
      _timestamp,
      _lastUpdate,
    )

    return borrowRate ? rateToApy(borrowRate) : 0n
  }

  /**
   * Lista DAO IRM: computes borrow APY with rate cap/floor clamping.
   *
   * Matches the on-chain InterestRateModel._borrowRate logic:
   * 1. Compute adaptive rate (same as Morpho)
   * 2. Resolve effective cap: rateCap (or DEFAULT_RATE_CAP if 0), floored by minCap
   * 3. Clamp avgRate to [rateFloor, cap]
   * 4. Clamp endRateAtTarget to cap/4
   */
  export function getListaBorrowApy(
    rateAtTarget: bigint | null,
    utilization: bigint,
    _timestamp: number = Math.floor(Date.now() / 1000),
    _lastUpdate: number,
    rateCap?: bigint,
    rateFloor?: bigint,
  ) {
    if (rateAtTarget == null || rateAtTarget === 0n) return 0n

    const elapsed = BigInt(_timestamp) - BigInt(_lastUpdate)
    if (elapsed < 0n) return 0n

    const { endBorrowRate } = _getListaBorrowRate(
      utilization,
      rateAtTarget,
      elapsed,
      rateCap,
      rateFloor,
    )

    return rateToApy(endBorrowRate)
  }

  /**
   * Lista-specific _getBorrowRate with cap/floor clamping.
   * Mirrors the Solidity InterestRateModel._borrowRate.
   */
  function _getListaBorrowRate(
    startUtilization: bigint,
    startRateAtTarget: bigint,
    elapsed: bigint,
    rateCap?: bigint,
    rateFloor?: bigint,
  ) {
    const errNormFactor =
      startUtilization > TARGET_UTILIZATION
        ? MathLib.WAD - TARGET_UTILIZATION
        : TARGET_UTILIZATION
    const err = MathLib.wDivDown(
      startUtilization - TARGET_UTILIZATION,
      errNormFactor,
    )

    let endRateAtTarget: bigint

    if (startRateAtTarget === 0n) {
      endRateAtTarget = INITIAL_RATE_AT_TARGET
    } else {
      const speed = MathLib.wMulDown(ADJUSTMENT_SPEED, err)
      const linearAdaptation = speed * elapsed

      if (linearAdaptation === 0n) {
        endRateAtTarget = startRateAtTarget
      } else {
        const _newRateAtTarget = (la: bigint) =>
          MathLib.min(
            MathLib.max(
              MathLib.wMulDown(startRateAtTarget, wExp(la)),
              MIN_RATE_AT_TARGET,
            ),
            MAX_RATE_AT_TARGET,
          )

        endRateAtTarget = _newRateAtTarget(linearAdaptation)
      }
    }

    // Resolve effective cap (matches Solidity: rateCap[id] != 0 ? rateCap[id] : DEFAULT_RATE_CAP)
    const cap = rateCap && rateCap > 0n ? rateCap : DEFAULT_RATE_CAP

    // Clamp endRateAtTarget to cap/4
    if (endRateAtTarget > cap / 4n) endRateAtTarget = cap / 4n

    const coeff =
      err < 0n
        ? MathLib.WAD - MathLib.wDivDown(MathLib.WAD, CURVE_STEEPNESS)
        : CURVE_STEEPNESS - MathLib.WAD

    const _curve = (rat: bigint) =>
      MathLib.wMulDown(MathLib.wMulDown(coeff, err) + MathLib.WAD, rat)

    let endBorrowRate = _curve(endRateAtTarget)

    // Clamp to [rateFloor, cap]
    if (endBorrowRate > cap) endBorrowRate = cap
    if (rateFloor && rateFloor > 0n && endBorrowRate < rateFloor)
      endBorrowRate = rateFloor

    return { endBorrowRate, endRateAtTarget }
  }

  // Lista default rate cap: 200% APR per second (WAD-scaled)
  // From ConstantsLib: uint256 internal constant DEFAULT_RATE_CAP = 634195839675291n (≈ 2000% APY)
  const DEFAULT_RATE_CAP = 634195839675291n

  const CURVE_STEEPNESS = 4_000000000000000000n
  const TARGET_UTILIZATION = 90_0000000000000000n
  const INITIAL_RATE_AT_TARGET = 4_0000000000000000n / SECONDS_PER_YEAR
  const ADJUSTMENT_SPEED = 50_000000000000000000n / SECONDS_PER_YEAR
  const MIN_RATE_AT_TARGET = 10_00000000000000n / SECONDS_PER_YEAR
  const MAX_RATE_AT_TARGET = 2_000000000000000000n / SECONDS_PER_YEAR

  /**
   * ln(2), scaled by WAD.
   */
  const LN_2_INT = 693147180559945309n

  /**
   * ln(1e-18), scaled by WAD.
   */
  const LN_WEI_INT = -41_446531673892822312n

  /**
   * Above this bound, `wExp` is clipped to avoid overflowing when multiplied with 1 ether.
   * This upper bound corresponds to: ln(type(int256).max / 1e36) (scaled by WAD, floored).
   */
  const WEXP_UPPER_BOUND = 93_859467695000404319n

  /**
   * The value of wExp(`WEXP_UPPER_BOUND`).
   */
  const WEXP_UPPER_VALUE =
    57716089161558943949701069502944508345128_422502756744429568n

  export function wExp(x: bigint) {
    x = BigInt(x)

    // If x < ln(1e-18) then exp(x) < 1e-18 so it is rounded to zero.
    if (x < LN_WEI_INT) return 0n
    // `wExp` is clipped to avoid overflowing when multiplied with 1 ether.
    if (x >= WEXP_UPPER_BOUND) return WEXP_UPPER_VALUE

    // Decompose x as x = q * ln(2) + r with q an integer and -ln(2)/2 <= r <= ln(2)/2.
    // q = x / ln(2) rounded half toward zero.
    const roundingAdjustment = x < 0n ? -(LN_2_INT / 2n) : LN_2_INT / 2n
    const q = (x + roundingAdjustment) / LN_2_INT
    const r = x - q * LN_2_INT

    // Compute e^r with a 2nd-order Taylor polynomial.
    const expR = MathLib.WAD + r + (r * r) / MathLib.WAD / 2n

    // Return e^x = 2^q * e^r.
    if (q >= 0n) return expR << q
    return expR >> -q
  }

  export function _getBorrowRate(
    startUtilization: bigint,
    startRateAtTarget: bigint,
    elapsed: bigint,
  ) {
    startUtilization = BigInt(startUtilization)
    startRateAtTarget = BigInt(startRateAtTarget)
    elapsed = BigInt(elapsed)

    const errNormFactor =
      startUtilization > TARGET_UTILIZATION
        ? MathLib.WAD - TARGET_UTILIZATION
        : TARGET_UTILIZATION
    const err = MathLib.wDivDown(
      startUtilization - TARGET_UTILIZATION,
      errNormFactor,
    )

    let avgRateAtTarget: bigint
    let endRateAtTarget: bigint

    if (startRateAtTarget === 0n) {
      // First interaction.
      avgRateAtTarget = INITIAL_RATE_AT_TARGET
      endRateAtTarget = INITIAL_RATE_AT_TARGET
    } else {
      // The speed is assumed constant between two updates, but it is in fact not constant because of interest.
      // So the rate is always underestimated.
      const speed = MathLib.wMulDown(ADJUSTMENT_SPEED, err)
      const linearAdaptation = speed * elapsed

      if (linearAdaptation === 0n) {
        // If linearAdaptation == 0, avgRateAtTarget = endRateAtTarget = startRateAtTarget;
        avgRateAtTarget = startRateAtTarget
        endRateAtTarget = startRateAtTarget
      } else {
        // Non negative because MIN_RATE_AT_TARGET > 0.
        const _newRateAtTarget = (linearAdaptation: bigint) =>
          MathLib.min(
            MathLib.max(
              MathLib.wMulDown(startRateAtTarget, wExp(linearAdaptation)),
              MIN_RATE_AT_TARGET,
            ),
            MAX_RATE_AT_TARGET,
          )

        // Formula of the average rate that should be returned to Morpho Blue:
        // avg = 1/T * ∫_0^T curve(startRateAtTarget*exp(speed*x), err) dx
        // The integral is approximated with the trapezoidal rule:
        // avg ~= 1/T * Σ_i=1^N [curve(f((i-1) * T/N), err) + curve(f(i * T/N), err)] / 2 * T/N
        // Where f(x) = startRateAtTarget*exp(speed*x)
        // avg ~= Σ_i=1^N [curve(f((i-1) * T/N), err) + curve(f(i * T/N), err)] / (2 * N)
        // As curve is linear in its first argument:
        // avg ~= curve([Σ_i=1^N [f((i-1) * T/N) + f(i * T/N)] / (2 * N), err)
        // avg ~= curve([(f(0) + f(T))/2 + Σ_i=1^(N-1) f(i * T/N)] / N, err)
        // avg ~= curve([(startRateAtTarget + endRateAtTarget)/2 + Σ_i=1^(N-1) f(i * T/N)] / N, err)
        // With N = 2:
        // avg ~= curve([(startRateAtTarget + endRateAtTarget)/2 + startRateAtTarget*exp(speed*T/2)] / 2, err)
        // avg ~= curve([startRateAtTarget + endRateAtTarget + 2*startRateAtTarget*exp(speed*T/2)] / 4, err)
        endRateAtTarget = _newRateAtTarget(linearAdaptation)
        avgRateAtTarget =
          (startRateAtTarget +
            endRateAtTarget +
            2n * _newRateAtTarget(linearAdaptation / 2n)) /
          4n
      }
    }

    // Non negative because 1 - 1/C >= 0, C - 1 >= 0.
    const coeff =
      err < 0
        ? MathLib.WAD - MathLib.wDivDown(MathLib.WAD, CURVE_STEEPNESS)
        : CURVE_STEEPNESS - MathLib.WAD

    const _curve = (rateAtTarget: bigint) =>
      MathLib.wMulDown(MathLib.wMulDown(coeff, err) + MathLib.WAD, rateAtTarget)

    // Non negative if avgRateAtTarget >= 0 because if err < 0, coeff <= 1.
    return {
      avgBorrowRate: _curve(avgRateAtTarget),
      endBorrowRate: _curve(endRateAtTarget),
      endRateAtTarget,
    }
  }
}
